/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

export interface BackupBundle {
  version: 1;
  exportedAt: string;
  settings: Record<string, unknown>;
}

/**
 * One-click local backup / restore of the entire app state. We read every
 * `matrix-setting-*` key from localStorage, bundle it, and let the user
 * download (and later re-import) the JSON. 100% local — no server.
 */

const PREFIX = "matrix-setting-";

export function exportBackup(): BackupBundle {
  const settings: Record<string, unknown> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) {
      try {
        settings[k] = JSON.parse(localStorage.getItem(k) ?? "null");
      } catch {
        settings[k] = localStorage.getItem(k);
      }
    }
  }
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    settings,
  };
}

export function downloadBackup(): void {
  const bundle = exportBackup();
  const blob = new Blob([JSON.stringify(bundle, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nevai-meet-backup-${bundle.exportedAt.slice(0, 10)}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function importBackup(json: string): { ok: boolean; message: string } {
  try {
    const bundle = JSON.parse(json) as BackupBundle;
    if (bundle.version !== 1 || !bundle.settings) {
      return { ok: false, message: "Unrecognized backup file." };
    }
    Object.entries(bundle.settings).forEach(([k, v]) => {
      localStorage.setItem(k, JSON.stringify(v));
    });
    return { ok: true, message: "Backup restored. Reload to apply." };
  } catch (e) {
    return { ok: false, message: String(e) };
  }
}

export function useDataBackup(): {
  download: () => void;
  import: (json: string) => { ok: boolean; message: string };
} {
  return { download: downloadBackup, import: importBackup };
}
